import { useInfiniteQuery } from '@tanstack/react-query';

import { useMemo } from 'react';

import type { ConversationMessage, GetConversationMessagesResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { conversationsEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

const MESSAGES_PAGE_LIMIT = 50;

const getConversationMessages = async (
	servicesProviderId: string,
	conversationId: string,
	cursor?: string,
): Promise<GetConversationMessagesResponse> => {
	const params: Record<string, unknown> = { limit: MESSAGES_PAGE_LIMIT };
	if (cursor) {
		params.afterCursor = cursor;
	}

	const response = await apiClient.get<GetConversationMessagesResponse>(
		conversationsEndpoints.messages(servicesProviderId, conversationId),
		{ params },
	);
	return response.data;
};

export interface ConversationMessagesResult {
	/** Flattened list of all fetched conversation messages. */
	messages: ConversationMessage[];
	/** Whether the initial page is still loading. */
	isLoading: boolean;
	/** Whether the most recent fetch encountered an error. */
	isError: boolean;
	/** Refetch messages from the beginning. */
	refetch: () => void;
	/** Whether more pages of messages are available. */
	hasNextPage: boolean;
	/** Whether the next page is currently being fetched. */
	isFetchingNextPage: boolean;
	/** Fetch the next page of messages. */
	fetchNextPage: () => void;
}

export const useGetConversationMessages = (
	leadId: string | undefined,
	conversationId: string | undefined,
): ConversationMessagesResult => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	const query = useInfiniteQuery({
		queryKey: queries.conversations.messages(
			servicesProviderId,
			leadId ?? '',
			conversationId ?? '',
		).queryKey,
		queryFn: async ({ pageParam }) => {
			if (!leadId || !conversationId) {
				throw new Error('Unable to fetch messages without a lead ID and conversation ID.');
			}
			return getConversationMessages(
				servicesProviderId,
				conversationId,
				pageParam as string | undefined,
			);
		},
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
		staleTime: 30_000,
		refetchOnWindowFocus: true,
		enabled: Boolean(leadId) && Boolean(conversationId) && Boolean(servicesProviderId),
		...errorBoundaryOptions,
		// Override throwOnError so message fetch failures are handled inline
		// via isError/refetch in ConversationView rather than the error boundary
		throwOnError: false,
	});

	const messages = useMemo<ConversationMessage[]>(() => {
		if (!query.data?.pages) return [];
		return query.data.pages.flatMap((page) => page.messages ?? []);
	}, [query.data?.pages]);

	return {
		messages,
		isLoading: query.isLoading,
		isError: query.isError,
		refetch: query.refetch,
		hasNextPage: query.hasNextPage ?? false,
		isFetchingNextPage: query.isFetchingNextPage,
		fetchNextPage: query.fetchNextPage,
	};
};
